import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Users, Shield, Mail, Image, TrendingUp } from 'lucide-react'
import NewsletterForm from './NewsletterForm'
import CampaignEmailForm from './CampaignEmailForm'

const AdminPanel = ({ onBack }) => {
  const { t } = useTranslation()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showNewsletter, setShowNewsletter] = useState(false)
  const [showCampaignEmail, setShowCampaignEmail] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [funnelData, setFunnelData] = useState(null)
  const [showFunnel, setShowFunnel] = useState(false)

  useEffect(() => {
    fetchUsers()
    fetchFunnel()
  }, [])

  const fetchFunnel = async () => {
    try {
      const response = await fetch('/api/v1/admin/page_views?days=14', {
        headers: { 'Content-Type': 'application/json' }
      })
      if (response.ok) {
        const data = await response.json()
        setFunnelData(data)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/v1/admin/users', {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        setUsers(data)
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to fetch users')
      }
    } catch (err) {
      setError('An error occurred while fetching users')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(users.map(u => u.id))
    } else {
      setSelectedIds([])
    }
  }

  const handleSelectUser = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return "データなし"
    return new Date(dateString).toLocaleDateString()
  }

  const formatGender = (gender) => {
    switch (gender) {
      case 'male': return '男性'
      case 'female': return '女性'
      default: return '未選択'
    }
  }

  if (loading) {
    return <div className="text-white p-4">Loading...</div>
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-lg">
          {error}
        </div>
        <button
          onClick={onBack}
          className="mt-4 flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          戻る
        </button>
      </div>
    )
  }

  if (showNewsletter) {
    return (
      <NewsletterForm
        onBack={() => setShowNewsletter(false)}
        selectedUserIds={selectedIds}
      />
    )
  }

  if (showCampaignEmail) {
    return (
      <CampaignEmailForm
        onBack={() => setShowCampaignEmail(false)}
        selectedUserIds={selectedIds}
      />
    )
  }

  return (
    <div className="w-full px-6 flex flex-col h-screen overflow-hidden">
      {/* Navigation Section (Fixed height) */}
      <div className="flex-none pt-6 pb-4 -mx-6 px-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-orange-500" />
              <h1 className="text-2xl font-bold text-white leading-none">管理画面</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-gray-800/50 px-4 py-2 rounded-xl border border-gray-700">
            <Users className="w-5 h-5 text-gray-400" />
            <span className="font-bold text-orange-500">{users.length}</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowNewsletter(true)}
            disabled={selectedIds.length === 0}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all text-sm ${
              selectedIds.length > 0
                ? 'bg-orange-600 hover:bg-orange-700 text-white border-b-4 border-orange-800 active:border-b-0 active:translate-y-1'
                : 'bg-gray-800 text-gray-500 cursor-not-allowed border-b-4 border-gray-900'
            }`}
          >
            <Mail className="w-4 h-4" />
            メールマガジン ({selectedIds.length})
          </button>
          <button
            onClick={() => setShowCampaignEmail(true)}
            disabled={selectedIds.length === 0}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all text-sm ${
              selectedIds.length > 0
                ? 'bg-blue-600 hover:bg-blue-700 text-white border-b-4 border-blue-800 active:border-b-0 active:translate-y-1'
                : 'bg-gray-800 text-gray-500 cursor-not-allowed border-b-4 border-gray-900'
            }`}
          >
            <Image className="w-4 h-4" />
            キャンペーンメール ({selectedIds.length})
          </button>
        </div>
      </div>

      {/* Funnel Section */}
      {funnelData && (
        <div className="flex-none mb-4">
          <button
            onClick={() => setShowFunnel(v => !v)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-2"
          >
            <TrendingUp className="w-4 h-4 text-orange-500" />
            <span className="font-bold text-white">登録ファネル（14日間）</span>
            <span className="text-gray-500">{showFunnel ? '▲' : '▼'}</span>
            <span className="ml-2 text-gray-500">
              LP {funnelData.totals.lp_page_views} → 登録ページ {funnelData.totals.signup_page_views} → 登録 {funnelData.totals.registrations}
              {funnelData.totals.lp_page_views > 0 && (
                <span className="ml-1 text-orange-400 font-bold">
                  CVR {Math.round(funnelData.totals.registrations / funnelData.totals.lp_page_views * 100)}%
                </span>
              )}
            </span>
          </button>

          {showFunnel && (
            <div className="bg-[#1c1c21] rounded-xl border border-gray-800 overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="px-4 py-2 text-xs text-gray-400 font-bold uppercase tracking-wider">日付</th>
                    <th className="px-4 py-2 text-xs text-gray-400 font-bold uppercase tracking-wider text-right">LP訪問</th>
                    <th className="px-4 py-2 text-xs text-gray-400 font-bold uppercase tracking-wider text-right">登録ページ</th>
                    <th className="px-4 py-2 text-xs text-gray-400 font-bold uppercase tracking-wider text-right">登録</th>
                    <th className="px-4 py-2 text-xs text-gray-400 font-bold uppercase tracking-wider text-right">CVR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {funnelData.daily.map((row) => {
                    const cvr = row.lp_page_views > 0
                      ? Math.round(row.registrations / row.lp_page_views * 100)
                      : null
                    return (
                      <tr key={row.date} className="hover:bg-[#2c2c2e]">
                        <td className="px-4 py-2 text-gray-300">{row.date}</td>
                        <td className="px-4 py-2 text-right text-gray-300">{row.lp_page_views}</td>
                        <td className="px-4 py-2 text-right text-gray-300">{row.signup_page_views}</td>
                        <td className="px-4 py-2 text-right text-gray-300">{row.registrations}</td>
                        <td className="px-4 py-2 text-right">
                          {cvr !== null ? (
                            <span className={`font-bold ${cvr >= 10 ? 'text-green-400' : cvr >= 3 ? 'text-orange-400' : 'text-gray-500'}`}>
                              {cvr}%
                            </span>
                          ) : (
                            <span className="text-gray-600">-</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Table Container (Scrollable) */}
      <div className="flex-grow min-h-0 bg-[#1c1c21] rounded-2xl border border-gray-800 overflow-hidden shadow-2xl flex flex-col mb-10">
        <div className="overflow-auto scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 z-30 bg-[#1c1c21] border-b border-gray-800">
              <tr className="bg-[#1c1c21]">
                <th className="p-4 w-12 bg-[#1c1c21]">
                  <input
                    type="checkbox"
                    checked={users.length > 0 && selectedIds.length === users.length}
                    onChange={handleSelectAll}
                    className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-orange-500 focus:ring-orange-500 focus:ring-offset-gray-900"
                  />
                </th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider bg-[#1c1c21] whitespace-nowrap">名前</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider bg-[#1c1c21] whitespace-nowrap">性別</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider bg-[#1c1c21] whitespace-nowrap">年齢</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider bg-[#1c1c21] whitespace-nowrap">Eメール</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center bg-[#1c1c21] whitespace-nowrap">プロバイダー</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider bg-[#1c1c21] whitespace-nowrap">登録日</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider bg-[#1c1c21] whitespace-nowrap">最終ログイン日</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider bg-[#1c1c21] whitespace-nowrap">最終トレーニング日</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center bg-[#1c1c21] whitespace-nowrap">有効期限</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center bg-[#1c1c21] whitespace-nowrap">メルマガ</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center bg-[#1c1c21] whitespace-nowrap">PWA</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider bg-[#1c1c21] whitespace-nowrap">最終AI使用日</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center bg-[#1c1c21] whitespace-nowrap">AIコーチ(今月)</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center bg-[#1c1c21] whitespace-nowrap">質問(今月)</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center bg-[#1c1c21] whitespace-nowrap">ワンポイント(今月)</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center bg-[#1c1c21] whitespace-nowrap">OCR(今月)</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider bg-[#1c1c21] whitespace-nowrap">最終ランニング日</th>
                <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider bg-[#1c1c21] whitespace-nowrap">流入元</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-[#2c2c2e] transition-colors">
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                      className="w-4 h-4 rounded border-gray-700 bg-[#2c2c2e] text-orange-500 focus:ring-orange-500 focus:ring-offset-[#1c1c1e]"
                    />
                  </td>
                  <td className="p-4 text-sm text-white">
                    {user.name || "-"}
                  </td>
                  <td className="p-4 text-sm text-gray-400">
                    {formatGender(user.gender)}
                  </td>
                  <td className="p-4 text-sm text-gray-400">
                    {user.age || "-"}
                  </td>
                  <td className="p-4 text-sm text-white font-medium">
                    {user.email}
                    {user.admin && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-orange-500/10 text-orange-500 border border-orange-500/20">
                        ADMIN
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-sm text-gray-400 capitalize">
                    {user.provider || "メールアドレス"}
                  </td>
                  <td className="p-4 text-sm text-gray-400">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="p-4 text-sm text-gray-400">
                    {formatDate(user.current_sign_in_at)}
                  </td>
                  <td className="p-4 text-sm text-gray-400">
                    {formatDate(user.last_training_date)}
                  </td>
                  <td className="p-4 text-sm text-gray-400">
                    {user.admin ? "" : formatDate(user.subscription_end_date)}
                  </td>
                  <td className="p-4 text-sm">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${user.receive_newsletter ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-gray-500/10 text-gray-500 border border-gray-500/20'}`}>
                      {user.receive_newsletter ? 'ON' : 'OFF'}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${user.pwa_installed ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-gray-500/10 text-gray-500 border border-gray-500/20'}`}>
                      {user.pwa_installed ? 'ON' : 'OFF'}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-gray-400">
                    {formatDate(user.last_ai_usage_date)}
                  </td>
                  <td className="p-4 text-sm text-center text-gray-400">
                    {user.ai_coach_this_month > 0 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-orange-500/10 text-orange-400 border border-orange-500/20">
                        {user.ai_coach_this_month}回
                      </span>
                    ) : '-'}
                  </td>
                  <td className="p-4 text-sm text-center text-gray-400">
                    {user.ai_question_this_month > 0 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-purple-500/10 text-purple-400 border border-purple-500/20">
                        {user.ai_question_this_month}回
                      </span>
                    ) : '-'}
                  </td>
                  <td className="p-4 text-sm text-center text-gray-400">
                    {user.one_point_this_month > 0 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-teal-500/10 text-teal-400 border border-teal-500/20">
                        {user.one_point_this_month}回
                      </span>
                    ) : '-'}
                  </td>
                  <td className="p-4 text-sm text-center text-gray-400">
                    {user.ocr_this_month > 0 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                        {user.ocr_this_month}回
                      </span>
                    ) : '-'}
                  </td>
                  <td className="p-4 text-sm text-gray-400">
                    {formatDate(user.last_running_date)}
                  </td>
                  <td className="p-4 text-sm text-gray-400 whitespace-nowrap">
                    {user.utm_source ? (
                      <span className="inline-flex flex-col gap-0.5">
                        <span className="text-blue-400 font-medium">{user.utm_source}</span>
                        {user.utm_campaign && <span className="text-xs text-gray-500">{user.utm_campaign}</span>}
                      </span>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default AdminPanel
